import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { CustomerPicker } from '@/features/customers';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromDraftCustomer, toCustomerBody } from '../api/mappers';
import { useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import {
  customerSchema,
  type CustomerFormInput,
  type CustomerFormOutput,
} from '../schemas/customer-schema';
import { isStateConflict } from '../schemas/draft-order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['customerId', 'email', 'phone']);

export interface DraftCustomerCardProps {
  storeId: string;
  draft: DraftOrder;
  editable: boolean;
}

/**
 * The shopper the quote is for. Picking a customer fills a blank email with theirs; the backend
 * answers 422 INVALID_REFERENCE on `customerId` for a customer of another store.
 */
export function DraftCustomerCard({ storeId, draft, editable }: DraftCustomerCardProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const { can } = usePermissions();
  const message = useErrorMessage();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:customer.saved');
  const form = useForm<CustomerFormInput, unknown, CustomerFormOutput>({
    resolver: useI18nResolver(customerSchema),
    defaultValues: fromDraftCustomer(draft),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: draft.id, body: toCustomerBody(values) });
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  const profileLink =
    draft.customerId && can('customers.read') ? (
      <Link
        to="/s/$storeId/customers/$customerId"
        params={{ storeId, customerId: draft.customerId }}
        className="text-sm hover:underline"
      >
        {t('draftOrders:customer.viewProfile')}
      </Link>
    ) : null;

  if (!editable) {
    return (
      <FormSection title={t('draftOrders:customer.title')} tour="draft-orders.detail.customer">
        <dl className="grid gap-2 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">{t('draftOrders:customer.email')}</dt>
            <dd className="break-all">{draft.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('draftOrders:customer.phone')}</dt>
            <dd>{draft.phone ?? '—'}</dd>
          </div>
        </dl>
        {profileLink}
      </FormSection>
    );
  }

  return (
    <FormSection title={t('draftOrders:customer.title')} tour="draft-orders.detail.customer">
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('draftOrders:customer.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="customerId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:customer.pick')}</FormLabel>
                <CustomerPicker
                  storeId={storeId}
                  value={field.value}
                  placeholder={t('draftOrders:customer.none')}
                  aria-invalid={fieldState.invalid}
                  onChange={(customer) => {
                    field.onChange(customer?.id ?? null);
                    if (customer && form.getValues('email').trim() === '') {
                      form.setValue('email', customer.email, { shouldDirty: true });
                    }
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:customer.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="off" {...field} />
                </FormControl>
                <FormDescription>{t('draftOrders:customer.emailHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:customer.phone')}</FormLabel>
                <FormControl>
                  <Input type="tel" autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-center justify-between gap-2">
            <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('draftOrders:customer.save')}
            </Button>
            {profileLink}
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
